/**
 * 软键盘打开时，用户用手滑动 agent 页则收起键盘，避免 iOS 上键盘期间
 * 拖动露出宿主页。
 *
 * 只认真实的 touchmove：键盘弹出本身会带来一串 scroll（onFocus 的
 * scrollIntoView、kb-open 改 padding 后的滚动钳制），拿 scroll 当信号会让
 * 键盘刚弹出就自己缩回去。
 */

const MOVE_THRESHOLD_PX = 12;
/** 点输入框聚焦的那一下手势不算滑动 */
const FOCUS_GRACE_MS = 300;

let suppressDismissUntil = 0;
let touchTracking = false;
let touchStartY = 0;
let touchStartX = 0;

function isTextInputFocused(): boolean {
  const tag = document.activeElement?.tagName;
  return tag === 'TEXTAREA' || tag === 'INPUT';
}

function dismissTextInputKeyboard(): void {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return;
  if (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT') el.blur();
}

function canDismissNow(): boolean {
  return isTextInputFocused() && Date.now() >= suppressDismissUntil;
}

function isIgnoredTarget(
  target: EventTarget | null,
  ignoreWithin: () => (HTMLElement | null)[],
): boolean {
  if (!(target instanceof Node)) return false;
  if (target === document.activeElement) return true;
  return ignoreWithin().some((root) => root?.contains(target));
}

export interface KeyboardDismissGestureOptions {
  /** 这些区域内的滑动不收键盘（如底部输入区） */
  ignoreWithin?: () => (HTMLElement | null)[];
}

/** 在 root 下监听滑动手势；返回卸载函数 */
export function installKeyboardDismissOnGesture(
  root: HTMLElement,
  options: KeyboardDismissGestureOptions = {},
): () => void {
  const ignoreWithin = options.ignoreWithin ?? (() => []);

  const onFocusIn = (e: FocusEvent) => {
    const tag = (e.target as HTMLElement | null)?.tagName;
    if (tag === 'TEXTAREA' || tag === 'INPUT') suppressDismissUntil = Date.now() + FOCUS_GRACE_MS;
  };

  const onTouchStart = (e: TouchEvent) => {
    touchTracking = false;
    if (!canDismissNow()) return;
    if (isIgnoredTarget(e.target, ignoreWithin)) return;
    touchTracking = true;
    touchStartY = e.touches[0]?.clientY ?? 0;
    touchStartX = e.touches[0]?.clientX ?? 0;
  };

  const onTouchMove = (e: TouchEvent) => {
    if (!touchTracking) return;
    if (!canDismissNow()) return;
    const y = e.touches[0]?.clientY ?? touchStartY;
    const x = e.touches[0]?.clientX ?? touchStartX;
    if (
      Math.abs(y - touchStartY) < MOVE_THRESHOLD_PX &&
      Math.abs(x - touchStartX) < MOVE_THRESHOLD_PX
    ) {
      return;
    }
    touchTracking = false;
    dismissTextInputKeyboard();
  };

  const onTouchEnd = () => {
    touchTracking = false;
  };

  root.addEventListener('focusin', onFocusIn);
  root.addEventListener('touchstart', onTouchStart, { passive: true, capture: true });
  root.addEventListener('touchmove', onTouchMove, { passive: true, capture: true });
  root.addEventListener('touchend', onTouchEnd, { passive: true, capture: true });
  root.addEventListener('touchcancel', onTouchEnd, { passive: true, capture: true });

  return () => {
    root.removeEventListener('focusin', onFocusIn);
    root.removeEventListener('touchstart', onTouchStart, true);
    root.removeEventListener('touchmove', onTouchMove, true);
    root.removeEventListener('touchend', onTouchEnd, true);
    root.removeEventListener('touchcancel', onTouchEnd, true);
  };
}
